import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createUser, getUserByEmail, getRegistrationOptions, RegistrationOptions } from '../services/api';
import { useLanguage } from '../contexts';
import LanguageSelector from '../components/common/LanguageSelector';
import { useTranslatedOptions } from '../utils/optionTranslations';

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(true);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState('');

  // Step 1 fields - Basic Info
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');

  // Step 2 fields - Language Profile (for ETALONNAGE)
  const [nativeLanguage, setNativeLanguage] = useState('');
  const [englishLevel, setEnglishLevel] = useState('');
  const [englishExposure, setEnglishExposure] = useState('');

  // Step 3 fields - Education & Career
  const [educationLevel, setEducationLevel] = useState('');
  const [studySpecialty, setStudySpecialty] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [workSector, setWorkSector] = useState('');
  const [dailyWritingPractice, setDailyWritingPractice] = useState('');

  // Login field
  const [loginEmail, setLoginEmail] = useState('');

  // Dropdown options from backend
  const [options, setOptions] = useState<RegistrationOptions | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await getRegistrationOptions();
        setOptions(response.data);
      } catch (err) {
        console.error('Failed to load options:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  // Call all hooks at top level (unconditionally)
  const sexOptions = useTranslatedOptions(options?.sex_options, 'sex', t) || [];
  const nativeLangOptions = useTranslatedOptions(options?.native_language_options, 'nativeLanguage', t) || [];
  const englishLevelOptions = useTranslatedOptions(options?.english_level_options, 'englishLevel', t) || [];
  const englishExposureOptions = useTranslatedOptions(options?.english_exposure_options, 'englishExposure', t) || [];
  const educationLevelOptions = useTranslatedOptions(options?.education_levels, 'educationLevel', t) || [];
  const studySpecialtyOptions = useTranslatedOptions(options?.study_specialties, 'studySpecialty', t) || [];
  const employmentStatusOptions = useTranslatedOptions(options?.employment_status, 'employmentStatus', t) || [];
  const workSectorOptions = useTranslatedOptions(options?.work_sectors, 'workSector', t) || [];
  const dailyWritingOptions = useTranslatedOptions(options?.daily_writing_options, 'dailyWriting', t) || [];

  const handleStep1Next = (e: React.FormEvent) => {
    e.preventDefault();
    if (fullName.trim() && email.trim() && age.trim() && sex) {
      setError('');
      setStep(2);
    }
  };

  const handleStep2Next = (e: React.FormEvent) => {
    e.preventDefault();
    if (nativeLanguage && englishLevel) {
      setError('');
      setStep(3);
    } else {
      setError(t('auth.fillRequired'));
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate mandatory fields in step 3
    if (!educationLevel || !studySpecialty || !employmentStatus || !dailyWritingPractice) {
      setError(t('auth.fillRequired'));
      return;
    }
    
    // If employed, work sector is mandatory
    if (!['etudiant', 'recherche_emploi', 'sans_emploi'].includes(employmentStatus) && !workSector) {
      setError(t('auth.selectSector'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await createUser({
        full_name: fullName,
        email,
        age: parseInt(age),
        sex,
        native_language: nativeLanguage,
        english_level: englishLevel,
        english_exposure: englishExposure || undefined,
        education_level: educationLevel,
        study_specialty: studySpecialty,
        employment_status: employmentStatus,
        work_sector: workSector || undefined,
        daily_writing_practice: dailyWritingPractice,
      });
      localStorage.setItem('userId', response.data.id.toString());
      localStorage.setItem('userFullName', response.data.full_name);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('auth.registrationSuccess'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await getUserByEmail(loginEmail);
      localStorage.setItem('userId', response.data.id.toString());
      localStorage.setItem('userFullName', response.data.full_name);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('auth.loginNotFound'));
    } finally {
      setLoading(false);
    }
  };

  // Check if work sector should be shown
  const showWorkSector = employmentStatus && !['etudiant', 'recherche_emploi', 'sans_emploi'].includes(employmentStatus);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-white to-secondary flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Language selector - fixed top-right corner */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSelector />
      </div>

      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm max-h-[90vh] overflow-y-auto">

        {/* Header with logo */}
        <div className="text-center mb-6">
          <img src="/logo.svg" alt="WordFlow Challenge" className="w-24 h-24 mx-auto mb-2 object-contain" />
          <h1 className="text-lg font-bold text-primary">
            WordFlow Challenge
          </h1>
        </div>

        {/* Toggle Inscription / Connexion */}
        <div className="mb-4 flex gap-2">
          <button
            type="button"
            onClick={() => { setIsRegister(true); setStep(1); setError(''); }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
              isRegister ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {t('auth.registration')}
          </button>
          <button
            type="button"
            onClick={() => { setIsRegister(false); setStep(1); setError(''); }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
              !isRegister ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {t('auth.login')}
          </button>
        </div>

        {/* Step indicator (registration only - 3 steps) */}
        {isRegister && (
          <div className="flex items-center justify-center gap-1 mb-4">
            {[1, 2, 3].map((s, idx) => (
              <React.Fragment key={s}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  step >= s ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
                }`}>{s}</div>
                {idx < 2 && <div className={`w-8 h-1 rounded ${step > s ? 'bg-primary' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Forms */}
        {isRegister ? (
          step === 1 ? (
            // Step 1: Basic Info
            <form onSubmit={handleStep1Next} className="space-y-3">
              <p className="text-xs text-gray-500 text-center mb-2">{t('auth.personalInfo')}</p>
              <div>
                <label className="block text-gray-700 text-xs font-semibold mb-1">
                  {t('auth.fullName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e: { target: { value: any; }; }) => setFullName(e.target.value)}
                  placeholder={t('auth.fullName')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-xs font-semibold mb-1">
                  {t('auth.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e: { target: { value: any; }; }) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-gray-700 text-xs font-semibold mb-1">
                    {t('auth.age')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={age}
                    onChange={(e: { target: { value: any; }; }) => setAge(e.target.value)}
                    placeholder={t('auth.age')}
                    min="10"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700 text-xs font-semibold mb-1">
                    {t('auth.sex')} <span className="text-red-500">*</span>
                  </label>
                  {loadingOptions ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50">
                      <span className="text-gray-400">...</span>
                    </div>
                  ) : (
                    <select
                      value={sex}
                      onChange={(e: { target: { value: any; }; }) => setSex(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                      required
                    >
                      <option value="">{t('auth.required')}</option>
                      {sexOptions.map((opt: { value: any; label: any; }) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                type="submit"
                className="w-full bg-primary text-white font-bold py-2.5 rounded-lg hover:bg-opacity-90 transition"
              >
                {t('auth.next')}
              </button>
            </form>

          ) : step === 2 ? (
            // Step 2: Language Profile (for score adjustments)
            <form onSubmit={handleStep2Next} className="space-y-3">
              <p className="text-xs text-gray-500 text-center mb-2">{t('auth.languageProfile')}</p>
              
              {loadingOptions ? (
                <div className="text-center py-4">
                  <div className="animate-spin inline-block w-5 h-5 border-2 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : options && (
                <>
                  <SelectField
                    label={t('auth.nativeLanguage')}
                    value={nativeLanguage}
                    onChange={setNativeLanguage}
                    options={nativeLangOptions}
                    placeholder="..."
                    required
                  />
                  <SelectField
                    label={t('auth.englishLevel')}
                    value={englishLevel}
                    onChange={setEnglishLevel}
                    options={englishLevelOptions}
                    placeholder="..."
                    required
                  />
                  <SelectField
                    label={t('auth.englishExposure')}
                    value={englishExposure}
                    onChange={setEnglishExposure}
                    options={englishExposureOptions}
                    placeholder="(...)"
                  />
                </>
              )}

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 border border-gray-300 text-gray-600 font-semibold py-2 rounded-lg hover:bg-gray-50 transition"
                >
                  {t('auth.back')}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white font-bold py-2 rounded-lg hover:bg-opacity-90 transition"
                >
                  {t('auth.next')}
                </button>
              </div>
            </form>

          ) : (
            // Step 3: Education & Career
            <form onSubmit={handleRegister} className="space-y-3">
              <p className="text-xs text-gray-500 text-center mb-2">{t('auth.educationCareer')}</p>

              {loadingOptions ? (
                <div className="text-center py-4">
                  <div className="animate-spin inline-block w-5 h-5 border-2 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : options && (
                <>
                  <SelectField
                    label={t('auth.educationLevel')}
                    value={educationLevel}
                    onChange={setEducationLevel}
                    options={educationLevelOptions}
                    placeholder="..."
                    required
                  />
                  <SelectField
                    label={t('auth.studySpecialty')}
                    value={studySpecialty}
                    onChange={setStudySpecialty}
                    options={studySpecialtyOptions}
                    placeholder="..."
                    required
                  />
                  <SelectField
                    label={t('auth.employmentStatus')}
                    value={employmentStatus}
                    onChange={setEmploymentStatus}
                    options={employmentStatusOptions}
                    placeholder="..."
                    required
                  />
                  {showWorkSector && (
                    <SelectField
                      label={t('auth.workSector')}
                      value={workSector}
                      onChange={setWorkSector}
                      options={workSectorOptions}
                      placeholder="..."
                      required
                    />
                  )}
                  <SelectField
                    label={t('auth.dailyWriting')}
                    value={dailyWritingPractice}
                    onChange={setDailyWritingPractice}
                    options={dailyWritingOptions}
                    placeholder="..."
                    required
                  />
                </>
              )}

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 border border-gray-300 text-gray-600 font-semibold py-2 rounded-lg hover:bg-gray-50 transition"
                >
                  {t('auth.back')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary text-white font-bold py-2 rounded-lg disabled:opacity-50 hover:bg-opacity-90 transition"
                >
                  {loading ? t('auth.redirecting') : t('auth.submit')}
                </button>
              </div>
            </form>
          )
        ) : (
          // Login Form
          <form onSubmit={handleLogin} className="space-y-3">
            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1">{t('auth.loginEmail')}</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e: { target: { value: any; }; }) => setLoginEmail(e.target.value)}
                placeholder="example@email.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white font-bold py-2.5 rounded-lg disabled:opacity-50 hover:bg-opacity-90 transition"
            >
              {loading ? t('auth.redirecting') : t('auth.loginButtonText')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Compact select field component
interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  required?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({ label, value, onChange, options, placeholder, required }) => (
  <div>
    <label className="block text-gray-700 text-xs font-semibold mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value}
      onChange={(e: { target: { value: any; }; }) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
      required={required}
    >
      <option value="">{placeholder}</option>
      {options.map((opt: { value: any; label: any; }) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

export default LandingPage;
